/**
 * Company lookup service — SIREN/SIRET search and enrichment.
 *
 * Implements Task 047: Company Lookup for Onboarding (SIREN/SIRET).
 *
 * Queries the control-api company endpoints which aggregate
 * 3 French government APIs (Recherche d'Entreprises, INSEE, INPI).
 */

// ---------------------------------------------------------------------------
// Response types (mirror backend Pydantic models)
// ---------------------------------------------------------------------------

export interface CompanyPreview {
  siren: string
  siret_siege: string | null
  name: string
  legal_form: string | null
  legal_form_code: string | null
  naf_code: string | null
  naf_label: string | null
  address: string | null
  postal_code: string | null
  city: string | null
  employee_range: string | null
  creation_date: string | null
  is_active: boolean
}

export interface Director {
  first_name: string | null
  last_name: string | null
  role: string | null
  since: string | null
  nationality: string | null
}

export interface Financials {
  revenue: number | null
  net_income: number | null
  fiscal_year: string | null
  source: string
}

export interface CompanyProfile {
  siren: string
  siret_siege: string | null
  name: string
  commercial_name: string | null

  legal_form: string | null
  legal_form_code: string | null
  naf_code: string | null
  naf_label: string | null
  economic_category: string | null

  address_street: string | null
  address_postal_code: string | null
  address_city: string | null
  address_country: string

  capital_social: number | null
  capital_currency: string
  tva_number: string | null

  employee_range: string | null
  creation_date: string | null
  is_active: boolean
  is_headquarters: boolean | null

  directors: Director[]
  financials: Financials | null
  establishments_count: number | null

  sources: Record<string, boolean>
  fetched_at: string | null
}

/**
 * CompanyData — the subset stored on an Organization.
 * Matches domain.auth.organization.CompanyData exactly.
 */
export interface CompanyData {
  siren: string | null
  siret_siege: string | null
  legal_name: string | null
  commercial_name: string | null
  legal_form: string | null
  legal_form_code: string | null
  naf_code: string | null
  naf_label: string | null
  address_street: string | null
  address_postal_code: string | null
  address_city: string | null
  address_country: string
  capital_social: number | null
  capital_currency: string
  tva_number: string | null
  employee_range: string | null
  creation_date: string | null
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class CompanyLookupService {
  private readonly baseUrl: string

  constructor() {
    // Uses Vite proxy: /api/control → control-api:8200
    this.baseUrl = '/api/control/v1'
  }

  /**
   * Autocomplete search by company name or SIREN/SIRET.
   */
  async searchCompanies(query: string, limit: number = 5): Promise<CompanyPreview[]> {
    const params = new URLSearchParams({ q: query, limit: String(limit) })

    const response = await fetch(`${this.baseUrl}/company/search?${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
      if (response.status === 422) return [] // validation error (query too short)
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Company search failed')
    }

    return response.json()
  }

  /**
   * Get full enriched company profile by SIREN (9 digits).
   */
  async getCompanyProfile(siren: string): Promise<CompanyProfile> {
    const response = await fetch(`${this.baseUrl}/company/${siren}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Company enrichment failed')
    }

    return response.json()
  }

  /**
   * Convert a CompanyProfile into the CompanyData shape stored on organizations.
   */
  profileToCompanyData(profile: CompanyProfile): CompanyData {
    return {
      siren: profile.siren,
      siret_siege: profile.siret_siege,
      legal_name: profile.name,
      commercial_name: profile.commercial_name,
      legal_form: profile.legal_form,
      legal_form_code: profile.legal_form_code,
      naf_code: profile.naf_code,
      naf_label: profile.naf_label,
      address_street: profile.address_street,
      address_postal_code: profile.address_postal_code,
      address_city: profile.address_city,
      address_country: profile.address_country,
      capital_social: profile.capital_social,
      capital_currency: profile.capital_currency,
      tva_number: profile.tva_number,
      employee_range: profile.employee_range,
      creation_date: profile.creation_date,
    }
  }
}

export const companyLookupService = new CompanyLookupService()
export default companyLookupService
